import { memo, useCallback, useMemo } from 'react';

import { useDispatch } from 'react-redux';

import { MODAL_MEDIA_PREVIEW } from '@/components/organisms/Modals/ModalConductor';
import { cn } from '@/lib/utils';
import { pushModal } from '@/redux/modals/actions';
import { Attachment as AttachmentType, AuthorType, ConversationSource } from '@/shared/types/chat';

import { Attachment } from './Attachment';

interface Props {
  attachments: AttachmentType[];
  source: ConversationSource;
  authorType: `${AuthorType}`;
  handleError?: (error?: Error) => void;
  align?: 'start' | 'end';
}

// Visual order: left (back/bottom) → right (front/top). First attachment is rightmost/on top.
const ROTATIONS = ['rotate-1', '-rotate-7', 'rotate-5', 'rotate-9'];
// Tailwind z-index classes per visual position — hover:z-50 overrides all of them
const Z_CLASSES = ['z-10', 'z-20', 'z-30', 'z-40'] as const;

export const ImageStack = memo(function ImageStack({
  attachments,
  source,
  authorType,
  handleError,
  align = 'end',
}: Props) {
  const dispatch = useDispatch();

  // Render last attachment first (leftmost, bottom) → first attachment last (rightmost, top)
  const ordered = useMemo(() => [...attachments].reverse(), [attachments]);

  const handleImageClick = useCallback(
    (data: { file: Buffer | string | null; url: string; mime_type: string; title?: string }) => {
      dispatch(
        pushModal(MODAL_MEDIA_PREVIEW, {
          url: data.url,
          file: data.file,
          mime_type: data.mime_type,
          title: data.title ?? '',
        })
      );
    },
    [dispatch]
  );

  return (
    <div
      className={cn(
        'isolate flex flex-wrap items-center pt-4',
        align === 'start' ? 'justify-start' : 'justify-end'
      )}
    >
      {ordered.map((attachment, index) => (
        <div
          key={attachment?.id ?? attachment.url}
          className={cn(
            'relative -mt-4 rounded-xl bg-card p-1.5 shadow-md',
            'cursor-pointer transition-all duration-200 ease-out',
            'hover:z-50 hover:-translate-y-1 hover:scale-[1.02] hover:rotate-0 hover:shadow-lg',
            ROTATIONS[index % ROTATIONS.length],
            Z_CLASSES[index] ?? 'z-10',
            index > 0 && '-ml-4'
          )}
        >
          <Attachment
            attachment={attachment}
            source={source}
            authorType={authorType}
            handleError={handleError}
            stackCard
            onImageClick={handleImageClick}
          />
        </div>
      ))}
    </div>
  );
});
